// A person's devices and pairing codes (ADR 0031, ADR 0064, docs/specs/phone-sign-in-and-devices.md
// sections 3.4 and 3.5). Shapes mirror crates/api-types/src/devices.rs. The pairing routes answer
// 501 until story A3 lands; the mock servers serve the contract.

import type { ApiTransport } from "./client.ts";
import type { Device, DeviceList, Timestamp, Uuid } from "./types.ts";

/** `PATCH /v1/devices/{id}`. Errors: 400 `name_taken`, `name_too_long`. */
export interface DevicePatch {
  name?: string;
  rings?: boolean;
}

/**
 * `POST /v1/me/pairing-codes`: a code that signs a phone in to the caller's own account. The
 * plain code is returned only here, once. Errors: 403 `reauth_required` (sign in again first),
 * `method_not_allowed`; 429 after 5 codes an hour.
 */
export interface PairingCode {
  id: Uuid;
  /** Shown as `K7MX-QP4R`. */
  code: string;
  /** `com.kitevoice.app://pair?code=K7MXQP4R`, drawn as the QR code. */
  link: string;
  expires_at: Timestamp;
}

/** One active device in the company, for admins (`GET /v1/admin/devices`). */
export interface AdminDevice {
  id: Uuid;
  user_id: Uuid;
  /** The person's name (their email when they have none). */
  person_name: string;
  person_email: string;
  /** The device's name. */
  name: string;
  kind: Device["kind"];
  app: string;
  os: string;
  app_version: string;
  last_seen_at: Timestamp;
  push: Device["push"];
}

export interface AdminDeviceList {
  items: AdminDevice[];
}

/** The longest name a device may have (the server answers `name_too_long` past it). */
export const DEVICE_NAME_MAX = 40;

/** A pairing code as people read it: `K7MXQP4R` and `k7mx-qp4r` both show `K7MX-QP4R`. */
export function displayPairingCode(code: string): string {
  const c = code.replace(/[^0-9a-z]/gi, "").toUpperCase();
  if (c.length < 8) return c;
  const half = Math.floor(c.length / 2);
  return `${c.slice(0, half)}-${c.slice(half)}`;
}

/** Whole seconds left before `expiresAt`, never below zero. */
export function pairingSecondsLeft(expiresAt: Timestamp, now: number): number {
  return Math.max(0, Math.ceil((Date.parse(expiresAt) - now) / 1000));
}

/** `m:ss`, for "Expires in 9:42". */
export function pairingClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * When a device was last used: "Now", "8 min ago", "3 hr ago", "Yesterday", "2 days ago", then
 * the date ("Oct 30"). `lower` gives the form that follows "Last used".
 */
export function deviceLastUsed(at: Timestamp, now: number, lower = false): string {
  const t = Date.parse(at);
  if (!Number.isFinite(t)) return "";
  const min = Math.floor((now - t) / 60_000);
  let s: string;
  if (min < 1) s = "Now";
  else if (min < 60) s = `${min} min ago`;
  else if (min < 24 * 60) s = `${Math.floor(min / 60)} hr ago`;
  else {
    const days = Math.floor(min / (24 * 60));
    if (days < 2) s = "Yesterday";
    else if (days < 7) s = `${days} days ago`;
    else {
      const d = new Date(t);
      return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
    }
  }
  return lower ? s.toLowerCase() : s;
}

const enc = encodeURIComponent;

export class DevicesApi {
  constructor(private readonly client: ApiTransport) {}

  /** The caller's active devices. */
  list(signal?: AbortSignal) {
    return this.client.get<DeviceList>("/v1/devices", signal);
  }
  update(id: string, patch: DevicePatch) {
    return this.client.patch<Device>(`/v1/devices/${enc(id)}`, patch);
  }
  /** Signs one of the caller's devices out: sessions revoked, credential deleted. */
  signOut(id: string) {
    return this.client.del<void>(`/v1/devices/${enc(id)}`);
  }
  /** A new pairing code; it closes the caller's earlier open one. */
  createPairingCode() {
    return this.client.post<PairingCode>("/v1/me/pairing-codes");
  }
  cancelPairingCode(id: string) {
    return this.client.del<void>(`/v1/me/pairing-codes/${enc(id)}`);
  }

  // Admins
  /** The company's active devices, most recently used first. */
  company(signal?: AbortSignal) {
    return this.client.get<AdminDeviceList>("/v1/admin/devices", signal);
  }
  revoke(id: string) {
    return this.client.post<void>(`/v1/admin/devices/${enc(id)}/revoke`);
  }
}
